<template>
  <div>
    <Control :is-export="true" @add="addItem" @remove="removeItems" @edit="editItem" @export="exportData" />
    <v-data-table
      v-model="selected"
      fixed-header
      :headers="headers"
      :items="agentsList"
      :items-per-page="50"
      :footer-props="{
        'items-per-page-options': [20, 50, 100, -1]
      }"
      :single-select="false"
      show-select
      height="600"
      class="agents-table"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
    >
      <template #header.firstname="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
          <TwoArrowSort class="agents-table--sort-icon" />
        </div>
      </template>

      <template #header.lastname="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
          <TwoArrowSort class="agents-table--sort-icon" />
        </div>
      </template>

      <template #header.profileName="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
          <TwoArrowSort class="agents-table--sort-icon" />
        </div>
      </template>

      <template #header.enterpriseName="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
          <TwoArrowSort class="agents-table--sort-icon" />
        </div>
      </template>

      <template #header.accessEndDate="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
          <TwoArrowSort class="agents-table--sort-icon" />
        </div>
      </template>

      <template #header.badge="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
          <TwoArrowSort class="agents-table--sort-icon" />
        </div>
      </template>

      <template #header.details="{ header }">
        <div class="d-flex justify-space-between align-center squeezeHeader">
          <span>{{ header.text }}</span>
        </div>
      </template>

      <template #item.lastname="{ item }">
        <span class="emphasizeNames">{{ item.lastname }}</span>
      </template>

      <template #item.firstname="{ item }">
        <span class="emphasizeNames">{{ item.firstname }}</span>
      </template>

      <template #item.accessBeginDate="{ item }">
        <span :class="statusTextAndClass(item)[1]">
          {{ statusTextAndClass(item)[0] }}
        </span>
      </template>

      <template #item.accessEndDate="{ item }">
        <span v-if="item.accessEndDate">{{ formatTime(item.accessEndDate) }}</span>
      </template>

      <template #item.actualSiteId="{ item }">
        <span v-if="item.actualSiteId" class="checkmark">{{ '✓' }}</span>
      </template>

      <template #item.badge="{ item }">
        <code v-if="item.badge">{{ item.badge }}</code>
      </template>

      <template #item.details="{ item }">
        <router-link :tag="'a'" :to="{ name: 'AgentsDetails', params: { id: item.id } }">
          <span>➡️</span>
        </router-link>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { json2excel } from 'js2excel';

import { formatValues } from '@/mixins/formatValues.mixin';
import Control from '@/components/sessions/Control';
import TwoArrowSort from '@/components/common/icons/TwoArrowSort';

export default {
  name: 'AgentsTable',
  components: {
    Control,
    TwoArrowSort
  },
  mixins: [formatValues],
  props: {
    data: {
      type: Array,
      default: () => []
    },
    profiles: {
      type: Array,
      default: () => []
    },
    enterprises: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selected: [],
      date: new Date().toISOString().slice(0, 10),
      sortBy: ['lastname', 'firstname'],
      sortDesc: false
    };
  },
  computed: {
    headers() {
      return [
        {
          text: 'Nom',
          divider: true,
          align: 'start',
          sortable: true,
          width: '15%',
          value: 'lastname'
        },
        {
          text: 'Prénom',
          divider: true,
          align: 'start',
          sortable: true,
          width: '13%',
          value: 'firstname'
        },
        {
          text: 'Entreprise',
          divider: true,
          align: 'start',
          sortable: true,
          width: '15%',
          value: 'enterpriseName'
        },
        {
          text: 'Profil',
          divider: true,
          align: 'start',
          sortable: true,
          width: '15%',
          value: 'profileName'
        },
        {
          text: 'Accès',
          divider: true,
          align: 'start',
          sortable: false,
          width: '15%',
          value: 'accessBeginDate'
        },
        {
          text: 'Fin de validité',
          divider: true,
          align: 'start',
          sortable: true,
          width: '9%',
          value: 'accessEndDate'
        },
        {
          text: 'Sur site',
          divider: true,
          align: 'start',
          sortable: true,
          width: '4%',
          value: 'actualSiteId'
        },
        {
          text: 'Badge',
          divider: true,
          align: 'start',
          sortable: true,
          width: '9%',
          value: 'badge'
        },
        {
          text: 'Détails',
          divider: true,
          align: 'start',
          sortable: false,
          width: '5%',
          value: 'details'
        }
      ];
    },
    agentsList() {
      return this.data.map(item => ({
        ...item
      }));
    },
    selectedIds() {
      return (this.selected || []).map(user => user.id);
    },
    selectedUsers() {
      return this.selectedIds.map(id => this.agentsList.find(u => u.id === id) || {});
    }
  },
  methods: {
    removeItems() {
      const number = this.selectedIds.length;

      // Aucun agent sélectionné, on ne fait rien
      if (!this.selectedIds || number === 0) {
        return;
      }

      const pluralized = number === 1 ? 'cet agent' : `ces ${number} agents`;
      const text = {
        title: 'Attention',
        subTitle: `Voulez-vous vraiment supprimer ${pluralized} ?`
      };

      // Pop-up de confirmation
      this.$modals.open({
        name: 'RemoveItem',
        payload: {
          text,
          submit: () => {
            this.$emit('remove', this.selectedIds);
            this.selected = [];
          }
        }
      });
    },
    editItem() {
      // No user selected
      if (!this.selectedIds || this.selectedIds.length !== 1) {
        return;
      }

      this.$modals.open({
        name: 'ChangeAgentModal',
        payload: {
          profiles: this.profiles,
          enterprises: this.enterprises,
          oldValues: {
            firstname: this.selectedUsers[0].firstname,
            lastname: this.selectedUsers[0].lastname,
            profile: this.selectedUsers[0].profileName,
            enterprise: this.selectedUsers[0].enterpriseName,
            badge: this.selectedUsers[0].badge || '',
            isLimitedAccess: this.selectedUsers[0].isLimitedAccess
          },
          submit: data => {
            this.$emit('edit', { id: this.selectedIds[0], data });
            this.selected = [];
          }
        }
      });
    },
    addItem() {
      this.$modals.open({
        name: 'ChangeAgentModal',
        payload: {
          profiles: this.profiles,
          enterprises: this.enterprises,
          oldValues: null,
          submit: data => {
            this.$emit('add', data);
          }
        }
      });
    },
    isAccessValid(date) {
      return new Date(this.date) < new Date(date);
    },
    statusTextAndClass(item) {
      if (this.isAccessValid(item.accessEndDate)) {
        return ['Autorisé', ''];
      }

      if (item.session) {
        if (item.session.result === null) {
          return ['Accueil prévu', 'access-soon'];
        }

        if (item.session.result === 0) {
          return ['Refusé - Accueil', 'access-forbidden'];
        }

        if (item.session.result === 1) {
          if (item.session.Interview === null || item.session.Interview.closeInterviewDate === null) {
            return ['Entretien prévu', 'access-interview'];
          }

          if (item.session.Interview.result === 0) {
            return ['Refusé - Entretien', 'access-forbidden'];
          }
        }
      }

      if (item.accessEndDate !== null) {
        return ['Expiré', 'access-expired'];
      }

      return ['', ''];
    },
    exportData() {
      const agentsList = this.data.map(item => {
        const agent = [
          ['Nom', item.lastname],
          ['Prénom', item.firstname],
          ['Entreprise', item.enterpriseName],
          ['Profil', item.profileName],
          ['Accès', item.accessBeginDate],
          ['Fin de validité', item.accessEndDate],
          ['Statut', item.accessStatus],
          ['Badge', item.badge]
        ];
        return Object.fromEntries(agent);
      });

      try {
        json2excel({
          data: agentsList,
          name: 'agents-list'
        });
      } catch (e) {
        console.error('export error');
      }
    }
  }
};
</script>

<style lang="scss">
.sessions-control-icon {
  padding: 0 4px;
}

.squeezeHeader {
  margin-left: -4px;
  margin-right: -10px;
}

.agents-table {
  .v-data-table-header__icon {
    display: none;
  }

  .access-forbidden {
    color: red;
  }
  .access-expired {
    color: dodgerblue;
    font-style: italic;
  }
  .access-soon {
    color: green;
  }
  .access-interview {
    color: darkorange;
  }

  a {
    color: #07a !important;
    text-decoration: none;
  }

  .v-input--selection-controls__input {
    .v-icon {
      color: #3dd598 !important;
    }
  }

  &--sort-icon {
    min-width: 24px;
    min-height: 24px;
  }

  tr {
    &:nth-child(odd) {
      background-color: #f2f2f2;
    }
    &:nth-child(1) {
      border-left: thin solid rgba(0, 0, 0, 0.12);
    }
    &:last-child {
      td {
        border-bottom: thin solid rgba(0, 0, 0, 0.12);
      }
    }

    td {
      .checkmark {
        color: #3dd598;
        font-size: 20px;
      }

      .emphasizeNames {
        color: rgba(30, 30, 30, 0.87);
        text-decoration: none;
        font-weight: bold;
      }
    }
  }

  thead {
    th {
      background-color: white;
      border-top: thin solid rgba(0, 0, 0, 0.12);

      &:nth-child(1) {
        border-left: thin solid rgba(0, 0, 0, 0.12);
      }
    }
  }
}
</style>
